// 7/8/2026
import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Search, Loader2 } from 'lucide-react'
import { dashboardAPI } from '../services/api'
import { useToast, getErrorMessage } from './Toast'
import { parsePaginatedResponse } from '../lib/pagination'
import { formatTime } from '../lib/dateFormat'
import Pagination from './Pagination'

export type EmployeeStatusFilter =
  | 'at_work'
  | 'on_leave'
  | 'absent'
  | 'checked_in_outside'
  | 'checked_out_outside'

const isGeofenceView = (s: EmployeeStatusFilter) => s === 'checked_in_outside' || s === 'checked_out_outside'

interface EmployeeStatusModalProps {
  status: EmployeeStatusFilter
  title: string
  onClose: () => void
}

const STATUS_BADGE_STYLES: Record<string, string> = {
  Present: 'bg-green-100 text-green-700',
  Late: 'bg-orange-100 text-orange-700',
  'Half Day': 'bg-yellow-100 text-yellow-700',
  'Missed Check Out': 'bg-rose-100 text-rose-700',
  'On Leave': 'bg-blue-100 text-blue-700',
  Absent: 'bg-red-100 text-red-700',
}

export default function EmployeeStatusModal({ status, title, onClose }: EmployeeStatusModalProps) {
  const { error: toastError } = useToast()
  const geofenceView = isGeofenceView(status)
  const [employees, setEmployees] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [hasPrev, setHasPrev] = useState(false)
  
  // Refs for managing debounce and mount state
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMounted = useRef(true)
  const initialLoadDone = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const load = useCallback(async (pageArg: number = page, searchArg: string = searchTerm, showLoading: boolean = true) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    if (showLoading && !initialLoadDone.current) {
      setIsLoading(true)
    }

    try {
      const res = await dashboardAPI.getEmployeesByStatus(status, pageArg, searchArg, {
        signal: abortController.signal
      })
      
      // Only update if component is mounted and request wasn't aborted
      if (!abortController.signal.aborted && isMounted.current) {
        const parsed = parsePaginatedResponse(res.data)
        setEmployees(parsed.results)
        setCount(parsed.count)
        setHasNext(parsed.hasNext)
        setHasPrev(parsed.hasPrev)
        
        if (!initialLoadDone.current) {
          initialLoadDone.current = true
        }
      }
    } catch (err: any) {
      // Ignore abort errors
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return
      }
      if (isMounted.current) {
        toastError(getErrorMessage(err, 'Failed to load employee list.'))
      }
    } finally {
      if (isMounted.current && !abortController.signal.aborted) {
        setIsLoading(false)
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
      }
    }
  }, [page, searchTerm, status, toastError])

  // Initial load on mount
  useEffect(() => {
    isMounted.current = true
    load(1, '', true)
    
    return () => {
      isMounted.current = false
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle page changes
  useEffect(() => {
    if (initialLoadDone.current) {
      load(page, searchTerm, false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  // Handle search with debounce
  useEffect(() => {
    if (!initialLoadDone.current) return

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1)
      load(1, searchTerm, false)
    }, 400)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  // Handle background click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div 
        className={`bg-white rounded-lg shadow-xl ${geofenceView ? 'max-w-5xl' : 'max-w-3xl'} w-full max-h-[85vh] flex flex-col animate-scale-in`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-secondary">{title}</h2>
            <p className="text-sm text-neutral-500">
              {count} employee{count === 1 ? '' : 's'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="text-neutral-500 hover:text-neutral-700 transition-colors p-1 rounded-lg hover:bg-neutral-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 pb-0 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search by name or employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-neutral-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary transition-shadow"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-neutral-600">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading employees...</span>
              </div>
            </div>
          ) : employees.length === 0 ? (
            <p className="text-center text-neutral-600 py-8">No employees found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 text-left text-neutral-500">
                    <th className="py-2 pr-4 font-medium">Sr. No.</th>
                    <th className="py-2 pr-4 font-medium">Employee ID</th>
                    <th className="py-2 pr-4 font-medium">Name</th>
                    {!geofenceView && <th className="py-2 pr-4 font-medium">Designation</th>}
                    <th className="py-2 pr-4 font-medium">Training Centre</th>
                    <th className="py-2 pr-4 font-medium">District</th>
                    {geofenceView && <th className="py-2 pr-4 font-medium">Check-in</th>}
                    {geofenceView && <th className="py-2 pr-4 font-medium">Check-out</th>}
                    <th className="py-2 pr-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp, index) => (
                    <tr key={emp.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                      <td className="py-3 pr-4 text-neutral-700">{index + 1}</td>
                      <td className="py-3 pr-4 font-medium text-neutral-900">{emp.employee_id}</td>
                      <td className="py-3 pr-4 text-neutral-700">{emp.full_name}</td>
                      {!geofenceView && <td className="py-3 pr-4 text-neutral-600">{emp.designation || '—'}</td>}
                      <td className="py-3 pr-4 text-neutral-600">{emp.training_centre_name || '—'}</td>
                      <td className="py-3 pr-4 text-neutral-600">{emp.district_name || '—'}</td>
                      {geofenceView && (
                        <td className="py-3 pr-4 text-neutral-700 whitespace-nowrap">
                          {formatTime(emp.check_in_time)}
                          {emp.check_in_distance_m != null && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-xs font-medium">
                              {emp.check_in_distance_m} m away
                            </span>
                          )}
                        </td>
                      )}
                      {geofenceView && (
                        <td className="py-3 pr-4 text-neutral-700 whitespace-nowrap">
                          {emp.check_out_time ? formatTime(emp.check_out_time) : '—'}
                          {emp.check_out_distance_m != null && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 text-xs font-medium">
                              {emp.check_out_distance_m} m away
                            </span>
                          )}
                        </td>
                      )}
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE_STYLES[emp.attendance_status] || 'bg-neutral-100 text-neutral-700'}`}>
                          {emp.attendance_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer with Pagination */}
        <div className="p-6 border-t border-neutral-200 flex-shrink-0">
          <Pagination 
            page={page} 
            onPageChange={setPage} 
            count={count} 
            hasNext={hasNext} 
            hasPrev={hasPrev} 
          />
        </div>
      </div>
    </div>
  )
}